import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, ArrowRight, AlertCircle } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import api from '@/lib/api';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await api.post('/admin/login', { username, password });
      
      // Store admin token separately
      localStorage.setItem('atlas_admin_token', response.data.token);
      toast.success('Admin login successful');
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid admin credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Minimal header */}
      <div className="border-b border-border px-6 h-14 flex items-center">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-red-500" strokeWidth={1.5} />
          <span className="font-heading text-lg font-semibold tracking-tight">Admin Portal</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-6 h-6 text-red-600" />
            </div>
            <h1 className="font-heading text-2xl font-semibold mb-1">Admin Access</h1>
            <p className="font-body text-sm text-muted-foreground">
              Restricted area. Authorized personnel only.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="username" className="font-body text-sm">Username</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Admin username"
                className="h-11 font-body"
                data-testid="admin-username"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="font-body text-sm">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Admin password"
                className="h-11 font-body"
                data-testid="admin-password"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-red-600 text-white hover:bg-red-700 font-body font-medium"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="spinner !w-4 !h-4 !border-white/30 !border-t-white" />
                  Authenticating...
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  Access Admin Panel <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-[11px] text-muted-foreground">
            This is a restricted area. All access attempts are logged.
          </p>
        </div>
      </div>
    </div>
  );
}
